import React, { useEffect, useState } from "react";
import Footer from "../components/Layout/Footer";
import { PublicHeader } from "../components/Layout/PublicHeader";
import DividerMotif from "../components/Brand/DividerMotif";
import { useDocumentMeta } from "../lib/useDocumentMeta";

/*
 * Velaigal /status page.
 *
 * Distinctive move: a single huge Fraunces timestamp is the visual anchor.
 * "Last checked" with the live time renders at clamp(80px, 10vw, 120px),
 * stamped above the page like a magazine masthead. The honesty IS the
 * design. You SEE that we just looked. The timestamp updates every second
 * so the page is genuinely live, not a static "all systems operational"
 * badge frozen from a deploy six weeks ago.
 *
 * Below the timestamp anchor, the page reads as a short editorial document:
 * the current honest state, then three commitments numbered like a
 * contract. No fake green dots. No mocked uptime metrics.
 */

const COMMITMENTS = [
  {
    title: "Public uptime",
    description:
      "We will publish real uptime numbers, refreshed every 60 seconds, against every major surface (web app, AI services, file storage, the screening renderer).",
  },
  {
    title: "Incident transparency",
    description:
      "Every customer-affecting incident gets a public timeline within 24 hours. Root cause within seven days.",
  },
  {
    title: "Maintenance windows",
    description:
      "Scheduled maintenance is announced at least seven days in advance, and never during Indian business hours unless safety demands it.",
  },
];

const formatTime = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const Status: React.FC = () => {
  const [now, setNow] = useState<Date>(() => new Date());

  useDocumentMeta(
    "Status · Velaigal",
    "The honest, live state of Velaigal's web app, AI services, and file storage — no frozen 'all systems operational' badges."
  );

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />

      {/* ---------------- Live timestamp anchor ----------------
       * The distinctive move. The visual subject of this page is the
       * timestamp itself. It updates every second so you can see the
       * page is genuinely live, not a frozen marketing badge. */}
      <section className="max-w-7xl mx-auto px-8 pt-20 pb-12">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="block w-2 h-2 rounded-full bg-saffron"
            style={{
              boxShadow: "0 0 0 4px rgba(194, 65, 12, 0.12)",
            }}
          />
          <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
            Status · Last checked
          </span>
        </div>

        <div className="mt-8">
          <time
            dateTime={now.toISOString()}
            className="font-display font-medium text-ink block leading-[0.95] tracking-tight"
            style={{
              fontSize: "clamp(5rem, 10vw, 7.5rem)",
              fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
              letterSpacing: "-0.04em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatTime(now)}
          </time>
          <span
            className="block text-base text-ink-tertiary mt-6 font-sans"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {formatDate(now)} · India Standard Time
          </span>
        </div>

        <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-10" />

        <h1
          className="font-display text-3xl lg:text-4xl font-medium text-ink leading-tight tracking-tight mt-10 max-w-3xl velaigal-headline-hover"
          style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
        >
          We are not pretending.
        </h1>
        <p className="mt-6 text-lg text-ink-secondary max-w-2xl leading-relaxed">
          Velaigal is in free early access. We do not yet publish live uptime
          numbers, because we do not yet have a probe pipeline that earns its
          green tick. When we do, this page becomes a real dashboard. Until
          then, here is the honest answer.
        </p>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- The current honest state ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Right now
            </span>
            <h2
              className="font-display text-3xl font-medium text-ink leading-snug mt-6 velaigal-headline-hover"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              Service is running.
            </h2>
            <p className="mt-6 text-base text-ink-secondary leading-relaxed">
              If you can read this page, the web app is up. Signup, job
              posting with AI task generation, and applications with AI
              pre-screening are all in active use by pilot customers.
            </p>
            <p className="mt-4 text-base text-ink-secondary leading-relaxed">
              If something is broken for you specifically, email{" "}
              <a
                href="mailto:im.vmanoj2002@gmail.com"
                className="velaigal-link"
                style={{ textDecoration: "none" }}
              >
                im.vmanoj2002@gmail.com
              </a>{" "}
              and we will respond personally.
            </p>
          </div>

          <div className="lg:pl-12 lg:border-l lg:border-hairline">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              When the dashboard arrives
            </span>
            <h2
              className="font-display text-3xl font-medium text-ink leading-snug mt-6 velaigal-headline-hover"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              You will see the real numbers.
            </h2>
            <p className="mt-6 text-base text-ink-secondary leading-relaxed">
              We are building the status surface next: real numbers,
              refreshed automatically. No marketing buffers, no rounding up,
              no "all systems operational" badges glued on with hope.
            </p>
          </div>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Commitments numbered like a contract ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24">
          <div className="flex items-baseline justify-between flex-wrap gap-6 mb-16">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Three things we will do
            </span>
            <span className="text-sm text-ink-tertiary italic font-display">
              Signed, the team at Velaigal
            </span>
          </div>

          <ol className="divide-y divide-hairline">
            {COMMITMENTS.map((commitment, idx) => (
              <li
                key={commitment.title}
                className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-4 py-12 first:pt-0 group"
              >
                <div className="lg:col-span-2">
                  <span
                    className="font-display block leading-none"
                    style={{
                      WebkitTextStroke: "1px var(--color-ink)",
                      WebkitTextFillColor: "transparent",
                      fontSize: "clamp(3rem, 5vw, 4.5rem)",
                      fontVariationSettings:
                        '"opsz" 144, "SOFT" 50, "WONK" 0',
                      letterSpacing: "-0.03em",
                    }}
                    aria-hidden="true"
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3
                  className="lg:col-span-4 font-display text-2xl lg:text-[28px] font-medium text-ink leading-snug velaigal-headline-hover"
                  style={{
                    fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
                  }}
                >
                  {commitment.title}
                </h3>
                <p className="lg:col-span-6 text-base text-ink-secondary leading-relaxed lg:pt-2">
                  {commitment.description}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Status;
